import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import {
	Reservation,
	Payment,
	PaymentsTerm,
	Document,
	CancellationReason,
	User,
	OfferFull,
	RoomType,
	Product,
	Convenience,
	Location,
	StopDefault,
	ProductKind,
	Catalog,
	OfferService,
	Board,
	Conversation,
	ConversationMessages,
	Attachment,
} from '../../reservation.model';

interface StoreInterface<T> {
	getters(state: T): {
		[K in keyof T]: (...args: any) => Observable<any>;
	};
	mutations(state: T): {
		[K in keyof T as `set${Capitalize<string & K>}`]: (payload: any) => void;
	};
}

interface State {
	reservation: BehaviorSubject<Reservation>;
	payment: BehaviorSubject<Payment[]>;
	paymentTerm: BehaviorSubject<PaymentsTerm[]>;
	document: BehaviorSubject<Document[]>;
	cancellationReasons: BehaviorSubject<CancellationReason[]>;
	loggedUser: BehaviorSubject<User>;
	users: BehaviorSubject<User[]>;
	offers: BehaviorSubject<OfferFull[]>;
	offerService: BehaviorSubject<OfferService[]>;
	conversations: BehaviorSubject<Conversation[]>;
	messages: BehaviorSubject<ConversationMessages[]>;
}

@Injectable({
	providedIn: 'root',
})
export class ReservationSummaryStoreService implements StoreInterface<State> {
	private readonly _state: State = {
		reservation: new BehaviorSubject(null),
		payment: new BehaviorSubject(null),
		paymentTerm: new BehaviorSubject(null),
		document: new BehaviorSubject(null),
		cancellationReasons: new BehaviorSubject([]),
		loggedUser: new BehaviorSubject(null),
		users: new BehaviorSubject([]),
		offers: new BehaviorSubject([]),
		offerService: new BehaviorSubject([]),
		conversations: new BehaviorSubject([]),
		messages: new BehaviorSubject([]),
	};

	boards: Board[] = [];
	roomTypes: RoomType[] = [];
	products: Product[] = [];
	conveniences: Convenience[] = [];
	locations: Location[] = [];
	stops: StopDefault[] = [];
	productKinds: ProductKind[] = [];
	promotions: Catalog[] = [];

	getters(state = this._state) {
		return {
			reservation: () => state.reservation.asObservable(),
			payment: () => state.payment.asObservable(),
			paymentTerm: () => state.paymentTerm.asObservable(),
			document: () => state.document.asObservable(),
			cancellationReasons: () => state.cancellationReasons.asObservable(),
			loggedUser: () => state.loggedUser.asObservable(),
			users: () => state.users.asObservable(),
			offers: () => state.offers.asObservable(),
			offerService: () => state.offerService.asObservable(),
			conversations: () => state.conversations.asObservable(),
			messages: () => state.messages.asObservable(),
		};
	}

	mutations(state = this._state) {
		return {
			setPayment: (payload: Payment[]) => {
				state.payment.next(payload);
			},
			setReservation: (payload: Reservation) => {
				state.reservation.next(payload);
			},
			setPaymentTerm: (payload: PaymentsTerm[]) => {
				state.paymentTerm.next(payload);
			},
			setDocument: (payload: Document[]) => {
				state.document.next(payload);
			},
			setCancellationReasons: (payload: CancellationReason[]) => {
				state.cancellationReasons.next(payload);
			},
			setOffers: (payload: OfferFull[]) => {
				state.offers.next(payload);
			},
			setOfferService: (payload: OfferService[]) => {
				state.offerService.next(payload);
			},
			setLoggedUser: (payload: User) => {
				state.loggedUser.next(payload);
			},
			setUsers: (payload: User[]) => {
				state.users.next(payload);
			},
			setConversations: (payload: Conversation[]) => {
				state.conversations.next(payload);
			},
			setMessages: (payload: ConversationMessages[]) => {
				state.messages.next(payload);
			},
		};
	}

	constructor() {}
}
